import type { DialogTitleClasses as MuiDialogTitleClasses } from '@mui/material/DialogTitle';
import type { DialogProps as MuiDialogProps } from '@mui/material/Dialog';
import type { QueryClient, Mutation } from '@tanstack/react-query';

import { useResponsive, Iconify, Button } from '@mfw/components';
import DialogContentText from '@mui/material/DialogContentText';
import { QueryClientContext } from '@tanstack/react-query';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { captureException } from '@sentry/react';
import MuiDialog from '@mui/material/Dialog';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import * as React from 'react';

import { isAnswerOf } from '../../context/answeredApiError';
import { describeErrorForTag } from '../../utils/describeErrorForTag';
import messages from '../../messages';
import Spinner from '../Spinner';
import './Dialog.scss';

/**
 * Whether the user has ALREADY been told about this failure — see `isAnswerOf`
 * for what counts as told.
 *
 * query-core records the error on the mutation BEFORE `mutateAsync` rejects, so
 * the very same object is in the cache by the time it reaches this dialog —
 * unless that same notification ended the session: `AuthProvider` answers a 401
 * with `queryClient.clear()` on the spot, and the mutation is gone before the
 * rejection lands (vid-1 #1). `settled` is what this confirm heard from the
 * cache while it ran, and it survives the clear.
 *
 * Checked, not assumed: every confirm handler today goes through a mutation,
 * but one that called an API function directly would arrive here with an error
 * nobody showed, and swallowing it would lose it without a trace.
 */
function wasAnsweredByMutation(
  error: unknown,
  queryClient: QueryClient | undefined,
  settled: ReadonlyMap<unknown, Mutation<unknown, unknown, unknown, unknown>>,
) {
  // Compared while `error` is still `unknown`: `state.error` is typed `Error`,
  // which `BaseApiResponseError` deliberately does not extend.
  const mutation =
    settled.get(error) ??
    queryClient
      ?.getMutationCache()
      .getAll()
      .find((cached) => cached.state.error === error);
  return isAnswerOf(error, mutation);
}

/**
 * Runs a confirm handler and OWNS whatever it returns (MYFITWORLD-V2-34).
 *
 * Almost every handler is `async` and awaits a `mutateAsync`, which rejects on
 * failure even after the mutation's own `onError` ran. Calling it and dropping
 * the result turned every refused confirm into an `unhandledrejection` — keyless
 * in Sentry, because `BaseApiResponseError` keeps its fields private — while the
 * user had in fact already seen the toast. Here, for all ~70 call sites at once:
 * a failure a mutation answered is done, anything else is a failure nobody
 * reported, and it goes to Sentry with the status/code it carries.
 *
 * The handler is still called synchronously — a Promise executor runs inline —
 * so a dialog that closes itself on confirm does so within the same click, and
 * a synchronous throw lands in the same `catch` as a rejection.
 */
function runConfirm(
  onConfirm: () => unknown,
  queryClient: QueryClient | undefined,
) {
  // Before the handler runs: a synchronous `mutate` settles inside it. Every
  // listener gets the notification even after an earlier one cleared the cache.
  const settled = new Map<
    unknown,
    Mutation<unknown, unknown, unknown, unknown>
  >();
  const unsubscribe = queryClient?.getMutationCache().subscribe((event) => {
    if (event.type === 'updated' && event.action.type === 'error') {
      settled.set(event.action.error, event.mutation);
    }
  });
  new Promise((resolve) => {
    resolve(onConfirm());
  }).then(
    () => unsubscribe?.(),
    (error: unknown) => {
      unsubscribe?.();
      if (wasAnsweredByMutation(error, queryClient, settled)) return;
      captureException(error, {
        tags: { dialogConfirmError: describeErrorForTag(error) },
      });
    },
  );
}

/**
 * Props for the Dialog component.
 */
export interface DialogProps extends Omit<MuiDialogProps, 'children' | 'open'> {
  classes?: { container?: string; paper?: string; root?: string };
  onCloseButtonClick?: () => void;
  /**
   * For a cancel button that is an action of its own ("Quit & Discard")
   * rather than a dismissal: it calls this instead of `onClose`, which then
   * only dismisses — Escape, the backdrop and browser back.
   */
  onCancelButtonClick?: () => void;
  /**
   * If true, the dialog will not close on backdrop click
   */
  disableBackdropClose?: boolean;

  /**
   * The description or additional information about the dialog.
   */
  description?: React.ReactNode;

  /**
   * data-testid for the confirm button
   */
  confirmButtonTestId?: string;

  /**
   * data-testid for the cancel/close button
   */
  cancelButtonTestId?: string;

  confirmButtonText?: string;

  withCloseButton?: boolean;

  closeButtonText?: string;

  withoutCancel?: boolean;
  /**
   * The content of the dialog.
   */
  children?: JSX.Element;
  /**
   * Callback function to be called when the user confirms or accepts the dialog.
   * May be `async`: the dialog owns the promise it returns (see `runConfirm`).
   */
  onConfirm?: () => unknown;
  /**
   * Callback function to be called when the dialog is closed.
   */
  onClose?: () => void;
  /**
   * Indicates that primary button should be loading
   */
  isLoading?: boolean;
  /**
   * Indicates that confirm action should be disabled
   */
  disabled?: boolean;
  /**
   * Indicates that dialog content should show a loading spinner.
   */
  pending?: boolean;
  /**
   * The title of the dialog.
   */
  title?: string;
  /**
   * Determines whether the dialog is open or closed.
   */
  open: boolean;
}

const Dialog = React.forwardRef(
  (props: DialogProps, ref: React.ForwardedRef<HTMLDivElement>) => {
    const { formatMessage } = useIntl();
    // Context, not `useQueryClient()`: that one throws outside a provider, and a
    // dialog must render wherever it is mounted. No client simply means no
    // mutation can have answered the failure, so it is reported.
    const queryClient = React.useContext(QueryClientContext);

    const isMobile = useResponsive('down', 'sm');

    const mobileRootClasses = React.useMemo(() => {
      const rootClasses = {
        container: classNames('Mfw-Dialog-container', props.classes?.container),
        paper: classNames('Mfw-Dialog-container', props.classes?.paper),
        root: props.classes?.root,
      };
      if (!isMobile) return rootClasses;
      return {
        ...rootClasses,
        paper: 'Mfw-Dialog-paper--mobile',
      };
    }, [isMobile, props.classes]);

    const handleClose = (
      e: React.MouseEvent<HTMLButtonElement>,
      source?: 'backdropClick' | 'closeButton',
    ) => {
      e.stopPropagation();
      if (source === 'backdropClick' && props.disableBackdropClose) return;
      if (props.isLoading) return;
      if (props.onClose) props.onClose();
    };

    const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!props.onCancelButtonClick) {
        handleClose(e);
        return;
      }
      e.stopPropagation();
      if (props.isLoading) return;
      props.onCancelButtonClick();
    };

    const handleConfirm = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (props.isLoading || props.disabled) return;
      if (props.onConfirm) runConfirm(props.onConfirm, queryClient);
    };

    const dialogTitleClasses = React.useMemo(() => {
      return {
        root: classNames('Mfw-Dialog-title', {
          'Mfw-Dialog-title--withCloseButton': props.withCloseButton,
        }),
      } as MuiDialogTitleClasses;
    }, [props.withCloseButton]);

    return (
      <MuiDialog
        onClose={(e: React.MouseEvent<HTMLButtonElement>) => {
          handleClose(e, 'backdropClick');
        }}
        className={classNames('Mfw-Dialog-root', props.className)}
        aria-describedby="alert-dialog-description"
        aria-labelledby="alert-dialog-title"
        classes={mobileRootClasses}
        open={props.open}
        ref={ref}
      >
        <DialogTitle classes={dialogTitleClasses} id="alert-dialog-title">
          {props.title}
          {props.withCloseButton && (
            <div className="">
              <IconButton
                onClick={props.onCloseButtonClick}
                data-testid="dialog-close-button"
                size="medium"
              >
                <Iconify icon="mdi:close" size="medium" />
              </IconButton>
            </div>
          )}
        </DialogTitle>
        <DialogContent classes={{ root: 'Mfw-DialogContent-root' }}>
          {!props.pending && props.description && (
            <DialogContentText id="alert-dialog-description" className="mb-5">
              {props.description}
            </DialogContentText>
          )}
          {props.pending && (
            <div className="w-full h-full flex items-center justify-center">
              <Spinner size="medium" />
            </div>
          )}
          {!props.pending && props.children}
        </DialogContent>
        <DialogActions className="Mfw-Dialog-actions">
          {!props.withoutCancel && (
            <Button
              data-testid={props.cancelButtonTestId}
              disabled={props.isLoading}
              onClick={handleCancel}
              fullWidth={isMobile}
              variant="outlined"
              destructive
            >
              {props?.closeButtonText || formatMessage(messages.cancel)}
            </Button>
          )}
          <Button
            data-testid={props.confirmButtonTestId}
            loading={props.isLoading}
            disabled={props.disabled}
            onClick={handleConfirm}
            fullWidth={isMobile}
            variant="contained"
            autoFocus
          >
            {props.confirmButtonText || formatMessage(messages.confirm)}
          </Button>
        </DialogActions>
      </MuiDialog>
    );
  },
);

Dialog.displayName = 'Dialog';
export default Dialog;
